import React, { useEffect, useState, useRef } from "react";
import { ymd, xlsYmd } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, Trash, Printer, Money, FileArrowUp, Download, WhatsappLogo, ArrowsClockwise, Warning, ClockCounterClockwise } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatIDR, formatDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { generatePDF } from "../lib/pdf";
import { downloadReceipt, buildWhatsAppReceiptURL } from "../lib/receiptPdf";
import Pagination from "../components/Pagination";

export default function Pembayaran() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [siswaList, setSiswaList] = useState([]);
  const [tagihans, setTagihans] = useState([]);
  const [NOMEN, setNOMEN] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [settings, setSettings] = useState({});
  const [orphanStats, setOrphanStats] = useState({ count: 0, total_amount: 0 });
  const [orphanDetail, setOrphanDetail] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importLembaga, setImportLembaga] = useState("");
  const [importFile2, setImportFile2] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesBusy, setBatchesBusy] = useState(false);
  const [rematchOpen, setRematchOpen] = useState(false);
  const [rematchPreview, setRematchPreview] = useState(null);
  const [rematchBusy, setRematchBusy] = useState(false);
  const today = ymd();
  const monthStart = today.slice(0, 8) + "01";
  const currentYear = new Date().getFullYear();
  const currentAcademicYear = new Date().getMonth() >= 6 ? currentYear : currentYear - 1;
  const [taFilter, setTaFilter] = useState("custom"); // 'custom' | '2026' | '2027' | ...
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [filterNomen, setFilterNomen] = useState("all");
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [searchSiswa, setSearchSiswa] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const fileInput = useRef();
  const canManage = ["superadmin","admin","finance"].includes(user?.role);

  const applyTaFilter = (taYear) => {
    setTaFilter(taYear);
    if (taYear === "custom") return;
    const y = Number(taYear);
    setStart(`${y}-07-01`);
    setEnd(`${y + 1}-06-30`);
  };

  const reqRef = useRef(0);
  useEffect(() => {
    Promise.all([
      api.get("/siswa"), api.get("/nomenklatur"),
      api.get("/lembaga").catch(() => ({ data: [] })),
      api.get("/settings").catch(() => ({ data: {} })),
    ]).then(([s, n, l, sett]) => { setSiswaList(s.data); setNOMEN(n.data); setLembagas(l.data); setSettings(sett.data); })
      .catch(() => {});
  }, []);
  const load = async () => {
    const rid = ++reqRef.current;
    const q = new URLSearchParams({ page: String(page), page_size: String(pageSize), start_date: start, end_date: end });
    if (filterNomen !== "all") q.set("nomenklatur_code", filterNomen);
    if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
    if (searchSiswa) q.set("q", searchSiswa);
    const [p, t, orp] = await Promise.all([
      api.get(`/pembayaran-paged?${q}`),
      api.get("/tagihan"),
      canManage ? api.get("/pembayaran/orphan-stats").catch(() => ({ data: { count: 0, total_amount: 0 } })) : Promise.resolve({ data: { count: 0, total_amount: 0 } }),
    ]);
    if (rid !== reqRef.current) return; // abaikan respons lama (filter sudah berubah)
    setItems(p.data.items || []);
    setTotal(p.data.total || 0);
    setTotalPages(p.data.total_pages || 1);
    setTagihans(t.data);
    setOrphanStats(orp.data || { count: 0, total_amount: 0 });
  };
  useEffect(() => { load(); }, [start, end, filterNomen, filterLembaga, page]);
  useEffect(() => { setPage(1); }, [start, end, filterNomen, filterLembaga]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 400); return () => clearTimeout(t); }, [searchSiswa]);

  const siswaMap = Object.fromEntries(siswaList.map((s) => [s.id, s]));

  const openNew = () => {
    setForm({ siswa_id: "", tagihan_id: "", nomenklatur_code: "IPP", amount: 0, payment_date: today, method: "cash", note: "", _lembaga_filter: filterLembaga !== "all" ? filterLembaga : "all", _siswa_search: "" });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount || 0), tagihan_id: form.tagihan_id || null };
      await api.post("/pembayaran", payload);
      toast.success("Pembayaran tercatat");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus pembayaran ini?")) return;
    try { await api.delete(`/pembayaran/${id}`); toast.success("Terhapus"); load(); } catch { toast.error("Gagal"); }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ["payment_date", "nis", "nomenklatur_code", "amount", "method", "teller", "note"],
      ["2025-01-15", "202500001", "IPP", 150000, "cash", "Nama Teller", "Pembayaran bulan Januari"],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, "Pembayaran");
    XLSX.writeFile(wb, "template-pembayaran.xlsx");
  };

  const importFile = async () => {
    if (!importLembaga) { toast.error("Pilih lembaga tujuan dulu"); return; }
    if (!importFile2) { toast.error("Pilih file Excel dulu"); return; }
    setImportBusy(true);
    try {
      const buf = await importFile2.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      if (!data.length) { toast.error("Excel kosong"); setImportBusy(false); return; }
      const valid = data.filter((d) => d.nis && d.amount && d.nomenklatur_code).map((d) => ({
        payment_date: (d.payment_date instanceof Date ? xlsYmd(d.payment_date) : String(d.payment_date)),
        nis: String(d.nis),
        nomenklatur_code: String(d.nomenklatur_code).toUpperCase(),
        amount: Number(d.amount),
        method: String(d.method || "cash"),
        teller: d.teller ? String(d.teller) : null,
        note: d.note ? String(d.note) : null,
      }));
      if (!valid.length) { toast.error("Tidak ada baris valid"); setImportBusy(false); return; }
      const { data: res } = await api.post(`/pembayaran/bulk?lembaga_id=${importLembaga}`, valid);
      toast.success(`${res.inserted} pembayaran ke ${res.lembaga_name} (batch ${res.import_batch_id})`);
      if (res.errors?.length) alert(`${res.errors.length} baris gagal:\n${res.errors.slice(0,10).map(e=>`NIS ${e.nis}: ${e.error}`).join("\n")}`);
      setImportOpen(false); setImportFile2(null); setImportLembaga("");
      load();
    } catch (e) { toast.error("Gagal import: " + formatApiError(e.response?.data?.detail || e.message)); }
    finally { setImportBusy(false); }
  };

  const openBatches = async () => {
    setBatchesOpen(true);
    setBatchesBusy(true);
    try {
      const { data } = await api.get("/pembayaran/import-batches");
      setBatches(data.batches || []);
    } catch (e) { toast.error("Gagal ambil batch: " + formatApiError(e.response?.data?.detail)); }
    finally { setBatchesBusy(false); }
  };

  const deleteBatch = async (batchId, count) => {
    if (!window.confirm(`Yakin hapus ${count} pembayaran dari batch ${batchId}?\nTagihan yang sudah dibayar akan di-rollback (paid_amount dikurangi).`)) return;
    try {
      const { data } = await api.delete(`/pembayaran/import-batch/${batchId}`);
      toast.success(`${data.deleted} pembayaran dihapus, ${data.rolled_back_tagihan} tagihan di-rollback`);
      openBatches();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totalSum = items.reduce((a, b) => a + (b.amount || 0), 0);
  const siswaTagihans = tagihans.filter((t) => t.siswa_id === form.siswa_id && t.status !== "paid");

  const visibleItems = items; // pagination + filter sekarang di server-side

  const buildReceiptArgs = (p) => {
    const siswa = siswaMap[p.siswa_id];
    const lembaga = lembagas.find((l) => l.id === (siswa?.lembaga_id || p.lembaga_id));
    const nomenklatur = NOMEN.find((n) => n.code === p.nomenklatur_code);
    // Compute sisa tagihan: if payment tied to tagihan, use that; else best-effort by nomen+siswa
    let sisa = -1;
    if (p.tagihan_id) {
      const tag = tagihans.find((t) => t.id === p.tagihan_id);
      if (tag) sisa = Math.max(0, (tag.amount || 0) - (tag.paid_amount || 0));
    }
    return { payment: p, siswa, lembaga, nomenklatur, settings, tagihan_sisa: sisa };
  };

  const printReceipt = async (p) => {
    try { await downloadReceipt(buildReceiptArgs(p)); toast.success("Kwitansi diunduh"); }
    catch (e) { toast.error("Gagal cetak: " + (e.message || "")); }
  };

  const sendWhatsApp = async (p) => {
    const siswa = siswaMap[p.siswa_id];
    if (!siswa?.parent_phone) {
      toast.warning("No HP wali murid belum diisi. Buka menu Siswa untuk lengkapi data.");
      return;
    }
    const url = buildWhatsAppReceiptURL(buildReceiptArgs(p));
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp dibuka — pesan sudah siap dikirim.");
  };

  const openRematchDialog = async () => {
    setRematchOpen(true);
    setRematchPreview(null);
    setOrphanDetail([]);
    setRematchBusy(true);
    try {
      const [preview, detail] = await Promise.all([
        api.post("/pembayaran/rematch?dry_run=true"),
        api.get("/pembayaran/orphan-detail?limit=200").catch(() => ({ data: { items: [] } })),
      ]);
      setRematchPreview(preview.data);
      setOrphanDetail(detail.data.items || []);
    } catch (e) {
      toast.error("Gagal preview: " + formatApiError(e.response?.data?.detail));
      setRematchOpen(false);
    } finally { setRematchBusy(false); }
  };

  const runRematch = async () => {
    setRematchBusy(true);
    try {
      const { data } = await api.post("/pembayaran/rematch?dry_run=false");
      toast.success(`${data.matched_count} pembayaran ter-link, ${data.tagihan_updated} tagihan diperbarui (Rp ${data.allocated_amount.toLocaleString("id-ID")}).`);
      setRematchOpen(false);
      load();
    } catch (e) {
      toast.error("Gagal rematch: " + formatApiError(e.response?.data?.detail));
    } finally { setRematchBusy(false); }
  };

  const printPDF = () => {
    generatePDF({
      title: "Laporan Pembayaran",
      subtitle: `${formatDate(start)} — ${formatDate(end)}`,
      headers: ["Tanggal", "No. Kwitansi", "Siswa", "Nomenklatur", "Metode", "Jumlah"],
      rows: items.map((p) => [
        formatDate(p.payment_date),
        p.receipt_no || "-",
        siswaMap[p.siswa_id]?.name || "-",
        p.nomenklatur_code,
        p.method,
        formatIDR(p.amount),
      ]),
      meta: { "Total": formatIDR(totalSum), "Jumlah Transaksi": items.length },
      filename: `pembayaran-${Date.now()}.pdf`,
    });
  };

  return (
    <div data-testid="pembayaran-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Keuangan</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Pembayaran Siswa</h1>
          <p className="text-gray-600 text-sm mt-1">Catat pembayaran harian dari siswa.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={printPDF} data-testid="pembayaran-print-button"><Printer size={16} className="mr-1.5" />Cetak PDF</Button>
          {canManage && <>
            <Button variant="outline" onClick={downloadTemplate} data-testid="pembayaran-template-button"><Download size={16} className="mr-1.5" />Template Excel</Button>
            <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e) => { if (e.target.files?.[0]) { setImportFile2(e.target.files[0]); setImportOpen(true); e.target.value = ""; }}} data-testid="pembayaran-import-file" />
            <Button variant="outline" onClick={()=>fileInput.current?.click()} data-testid="pembayaran-import-button"><FileArrowUp size={16} className="mr-1.5" />Import Excel</Button>
            <Button variant="outline" onClick={openBatches} data-testid="pembayaran-batches-button" className="border-amber-300 text-amber-700 hover:bg-amber-50"><ClockCounterClockwise size={16} className="mr-1.5" />Riwayat Import</Button>
            <Button variant="outline" onClick={openRematchDialog} data-testid="pembayaran-rematch-button" className={orphanStats.count > 0 ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}>
              <ArrowsClockwise size={16} className="mr-1.5" />Rematch{orphanStats.count > 0 && <span className="ml-1.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{orphanStats.count}</span>}
            </Button>
            <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="pembayaran-add-button"><Plus size={16} className="mr-1.5" />Catat Pembayaran</Button>
          </>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Total Pemasukan</div>
          <div className="font-mono text-2xl font-bold text-[#059669] mt-1">{formatIDR(totalSum)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Jumlah Transaksi</div>
          <div className="font-mono text-2xl font-bold text-gray-900 mt-1">{items.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Rata-rata</div>
          <div className="font-mono text-2xl font-bold text-gray-900 mt-1">{formatIDR(items.length ? totalSum / items.length : 0)}</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs">Tahun Ajaran</Label>
            <Select value={taFilter} onValueChange={applyTaFilter}>
              <SelectTrigger className="w-56 bg-white" data-testid="pembayaran-ta-filter"><SelectValue placeholder="Pilih TA" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="custom">Custom (Pilih Tanggal Sendiri)</SelectItem>
                {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>TA {y}/{y + 1} (Jul {y} – Jun {y + 1})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e)=>{setStart(e.target.value); setTaFilter("custom");}} data-testid="pembayaran-start-date" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e)=>{setEnd(e.target.value); setTaFilter("custom");}} data-testid="pembayaran-end-date" /></div>
          <div><Label className="text-xs">Lembaga</Label>
            <Select value={filterLembaga} onValueChange={setFilterLembaga}>
              <SelectTrigger className="w-52 bg-white" data-testid="pembayaran-lembaga-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">Semua Lembaga</SelectItem>
                {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Cari Siswa</Label>
            <Input value={searchSiswa} onChange={(e)=>setSearchSiswa(e.target.value)} placeholder="🔍 nama/NIS..." className="w-56 bg-white" data-testid="pembayaran-search-siswa" />
          </div>
          <div><Label className="text-xs">Nomenklatur</Label>
            <Select value={filterNomen} onValueChange={setFilterNomen}>
              <SelectTrigger className="w-44 bg-white" data-testid="pembayaran-nomen-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">Semua Nomenklatur</SelectItem>
                {NOMEN.map((n)=><SelectItem key={n.code} value={n.code}>{n.code} - {n.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-gray-500 font-mono ml-auto self-center">Hal {page}/{totalPages} · {total}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Tanggal</th>
                <th className="px-4 py-3 font-semibold">No. Kwitansi</th>
                <th className="px-4 py-3 font-semibold">Siswa</th>
                <th className="px-4 py-3 font-semibold">Nomenklatur</th>
                <th className="px-4 py-3 font-semibold">Metode</th>
                <th className="px-4 py-3 font-semibold text-right">Jumlah</th>
                <th className="px-4 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono text-xs">{formatDate(p.payment_date)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{p.receipt_no || "-"}</td>
                  <td className="px-4 py-2.5 font-semibold">{siswaMap[p.siswa_id]?.name || "-"}</td>
                  <td className="px-4 py-2.5"><span className="inline-block px-2 py-0.5 rounded bg-[#CCFBF1] text-[#0B8D95] text-xs font-mono font-semibold">{p.nomenklatur_code}</span></td>
                  <td className="px-4 py-2.5 capitalize text-xs">{p.method}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-[#059669]">{formatIDR(p.amount)}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => printReceipt(p)} className="p-1.5 hover:bg-[#0B8D95]/10 rounded text-[#0B8D95]" title="Cetak Kwitansi" data-testid={`pembayaran-print-${p.id}`}><Printer size={14} weight="duotone" /></button>
                    <button onClick={()=>sendWhatsApp(p)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Kirim via WhatsApp" data-testid={`pembayaran-wa-${p.id}`}><WhatsappLogo size={14} weight="fill" /></button>
                    {canManage && <button onClick={()=>remove(p.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`pembayaran-delete-${p.id}`}><Trash size={14} /></button>}
                  </td>
                </tr>
              ))}
              {visibleItems.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">Belum ada pembayaran</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onChange={setPage} testIdPrefix="pembayaran" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="pembayaran-dialog">
          <DialogHeader><DialogTitle>Catat Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lembaga</Label>
                <Select value={form._lembaga_filter || "all"} onValueChange={(v)=>setForm({...form, _lembaga_filter: v, siswa_id: "", tagihan_id: ""})}>
                  <SelectTrigger className="bg-white" data-testid="pembayaran-form-lembaga"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="all">Semua Lembaga</SelectItem>
                    {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Cari Siswa</Label>
                <Input value={form._siswa_search || ""} onChange={(e)=>setForm({...form, _siswa_search: e.target.value})} placeholder="🔍 nama/NIS..." data-testid="pembayaran-form-search" />
              </div>
            </div>
            <div><Label>Siswa *</Label>
              <Select value={form.siswa_id} onValueChange={(v)=>setForm({...form, siswa_id: v, tagihan_id: ""})}>
                <SelectTrigger className="bg-white" data-testid="pembayaran-siswa-select"><SelectValue placeholder="Pilih siswa" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {siswaList.filter((s)=> {
                    if (form._lembaga_filter && form._lembaga_filter !== "all" && s.lembaga_id !== form._lembaga_filter) return false;
                    const q = (form._siswa_search || "").trim().toLowerCase();
                    if (q) { const hay = `${s.name||""} ${s.nis||""} ${s.nisn||""}`.toLowerCase(); if (!hay.includes(q)) return false; }
                    return true;
                  }).slice(0, 100).map((s)=><SelectItem key={s.id} value={s.id}>{s.nis} - {s.name} <span className="text-xs text-gray-500">({s.class_name || "-"})</span></SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-gray-500 mt-1">Pilih lembaga atau ketik nama/NIS di "Cari Siswa" untuk mempersempit. Max 100 hasil tampil.</div>
            </div>
            {siswaTagihans.length > 0 && (
              <div><Label>Bayar Tagihan (opsional)</Label>
                <Select value={form.tagihan_id || "none"} onValueChange={(v)=>{
                  if (v === "none") { setForm({...form, tagihan_id: "" }); return; }
                  const tag = siswaTagihans.find((t) => t.id === v);
                  setForm({...form, tagihan_id: v, nomenklatur_code: tag?.nomenklatur_code || form.nomenklatur_code, amount: (tag?.amount || 0) - (tag?.paid_amount || 0) });
                }}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">— Tanpa tagihan —</SelectItem>
                    {siswaTagihans.map((t) => (<SelectItem key={t.id} value={t.id}>{t.nomenklatur_code} - {t.period} ({formatIDR(t.amount - t.paid_amount)})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomenklatur</Label>
                <Select value={form.nomenklatur_code} onValueChange={(v)=>setForm({...form, nomenklatur_code: v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{NOMEN.map((n)=><SelectItem key={n.code} value={n.code}>{n.code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Metode</Label>
                <Select value={form.method || "cash"} onValueChange={(v)=>setForm({...form, method: v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="cash">Tunai</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="qris">QRIS</SelectItem>
                    <SelectItem value="ewallet">E-Wallet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Jumlah *</Label><Input type="number" value={form.amount || 0} onChange={(e)=>setForm({...form, amount: e.target.value})} data-testid="pembayaran-amount-input" /></div>
              <div><Label>Tanggal *</Label><Input type="date" value={form.payment_date} onChange={(e)=>setForm({...form, payment_date: e.target.value})} /></div>
            </div>
            <div><Label>Catatan</Label><Input value={form.note || ""} onChange={(e)=>setForm({...form, note: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="pembayaran-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={importOpen} onOpenChange={(v) => { if (!importBusy) { setImportOpen(v); if (!v) { setImportFile2(null); setImportLembaga(""); } }}}>
        <DialogContent className="bg-white" data-testid="pembayaran-import-dialog">
          <DialogHeader><DialogTitle>Import Pembayaran dari Excel</DialogTitle></DialogHeader>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 flex gap-2 items-start">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div><b>Pilih Lembaga tujuan WAJIB</b> — supaya NIS di-lookup hanya di siswa lembaga tsb. Kalau NIS sama di 2 sekolah, tanpa filter ini pembayaran bisa nyasar.</div>
          </div>
          <div className="space-y-3 mt-3">
            <div><Label>File Excel</Label><div className="text-sm p-2 bg-gray-50 rounded border">📄 {importFile2?.name || "(belum dipilih)"}</div></div>
            <div><Label>Lembaga Tujuan *</Label>
              <Select value={importLembaga} onValueChange={setImportLembaga}>
                <SelectTrigger className="bg-white" data-testid="pay-import-lembaga-select"><SelectValue placeholder="-- Pilih Lembaga --" /></SelectTrigger>
                <SelectContent className="bg-white">{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportFile2(null); setImportLembaga(""); }} disabled={importBusy}>Batal</Button>
            <Button onClick={importFile} disabled={importBusy || !importLembaga || !importFile2} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="pay-import-confirm-button">{importBusy ? "Mengimport..." : "Import Sekarang"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batchesOpen} onOpenChange={setBatchesOpen}>
        <DialogContent className="bg-white max-w-4xl" data-testid="pembayaran-batches-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={20} className="text-amber-700" />Riwayat Import Pembayaran</DialogTitle></DialogHeader>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 mb-3">
            Klik 🗑 untuk hapus semua pembayaran dari 1 batch. Tagihan yang sudah dibayar dari batch tsb akan **auto rollback** (paid_amount dikurangi, status kembali unpaid/partial).
          </div>
          {batchesBusy ? <div className="p-6 text-center text-muted-foreground">Memuat...</div> : (
            <div className="max-h-96 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0"><tr className="text-left">
                  <th className="px-3 py-2 font-bold">Waktu Import</th>
                  <th className="px-3 py-2 font-bold">Lembaga</th>
                  <th className="px-3 py-2 font-bold text-right">Jml Pembayaran</th>
                  <th className="px-3 py-2 font-bold text-right">Total Rp</th>
                  <th className="px-3 py-2 font-bold text-center">Aksi</th>
                </tr></thead>
                <tbody>
                  {batches.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Belum ada batch import</td></tr>}
                  {batches.map((b) => (
                    <tr key={b.batch_id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{(b.first_created || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="px-3 py-2 font-semibold">{b.lembaga_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{b.count}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatIDR(b.sum_amount || 0)}</td>
                      <td className="px-3 py-2 text-center">
                        <Button size="sm" variant="outline" onClick={() => deleteBatch(b.batch_id, b.count)} data-testid={`pay-batch-delete-${b.batch_id}`} className="text-rose-600 border-rose-300 hover:bg-rose-50"><Trash size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setBatchesOpen(false)}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rematchOpen} onOpenChange={setRematchOpen}>
        <DialogContent className="bg-white max-w-3xl" data-testid="rematch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowsClockwise size={20} className="text-[#0B8D95]" />Rematch Pembayaran ke Tagihan</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-900 text-xs">
              <b>Apa ini?</b> Fitur ini mencari pembayaran yang belum ter-link ke tagihan (misal: bulk import DKG/PSB sebelum tagihan dibuat), lalu otomatis menghubungkannya ke tagihan terbuka yang cocok (NIS + Nomenklatur). Alokasi dimulai dari periode terlama.
            </div>

            {rematchBusy && !rematchPreview ? (
              <div className="p-6 text-center text-muted-foreground">Menganalisis pembayaran orphan...</div>
            ) : rematchPreview ? (
              <>
                <div className="border border-gray-200 rounded-md divide-y">
                  <div className="flex justify-between p-2.5"><span>Total pembayaran orphan</span><b className="font-mono">{rematchPreview.orphan_total}</b></div>
                  <div className="flex justify-between p-2.5"><span>Akan di-match</span><b className="font-mono text-emerald-700">{rematchPreview.matched_count}</b></div>
                  <div className="flex justify-between p-2.5"><span>Tidak ada tagihan cocok</span><b className="font-mono text-amber-700">{rematchPreview.unmatched_count}</b></div>
                  <div className="flex justify-between p-2.5"><span>Jumlah dialokasi</span><b className="font-mono text-[#0B8D95]">{formatIDR(rematchPreview.allocated_amount)}</b></div>
                  <div className="flex justify-between p-2.5"><span>Tagihan diperbarui</span><b className="font-mono">{rematchPreview.tagihan_updated}</b></div>
                </div>

                {orphanDetail.length > 0 && (
                  <div>
                    <div className="text-xs font-bold text-gray-700 mb-1.5 flex items-center gap-2">
                      <span>Detail Pembayaran Orphan ({orphanDetail.length})</span>
                      <span className="text-[10px] font-normal text-gray-500">— hover baris untuk lihat sebab</span>
                    </div>
                    <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr className="text-left">
                            <th className="px-2 py-2 font-bold">Tgl</th>
                            <th className="px-2 py-2 font-bold">NIS</th>
                            <th className="px-2 py-2 font-bold">Siswa</th>
                            <th className="px-2 py-2 font-bold">Nomen.</th>
                            <th className="px-2 py-2 font-bold text-right">Jumlah</th>
                            <th className="px-2 py-2 font-bold text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanDetail.map((o) => {
                            const meta = o.reason === "ready"
                              ? { label: "SIAP MATCH", cls: "bg-emerald-100 text-emerald-700", tip: "Ada tagihan open — akan di-link saat Jalankan Rematch" }
                              : o.reason === "all_paid"
                              ? { label: "SEMUA PAID", cls: "bg-amber-100 text-amber-700", tip: "Semua tagihan siswa+nomenklatur ini sudah lunas — kemungkinan duplikat / overpay" }
                              : { label: "TAGIHAN TIDAK ADA", cls: "bg-rose-100 text-rose-700", tip: "Buat tagihan dulu di menu Tagihan → siswa & nomenklatur ini belum punya tagihan" };
                            return (
                              <tr key={o.id} className="border-t hover:bg-gray-50" title={meta.tip}>
                                <td className="px-2 py-1.5 font-mono">{o.payment_date}</td>
                                <td className="px-2 py-1.5 font-mono">{o.nis}</td>
                                <td className="px-2 py-1.5">{o.siswa_name}<div className="text-[10px] text-gray-500">{o.class_name}</div></td>
                                <td className="px-2 py-1.5"><span className="inline-block px-1.5 py-0.5 rounded bg-[#CCFBF1] text-[#0B8D95] font-mono font-bold">{o.nomenklatur_code}</span></td>
                                <td className="px-2 py-1.5 text-right font-mono font-bold">{formatIDR(o.amount)}</td>
                                <td className="px-2 py-1.5 text-center"><span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-black ${meta.cls}`}>{meta.label}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : null}

            {rematchPreview && rematchPreview.matched_count === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-900 text-xs flex gap-2 items-start">
                <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                <div>
                  <b>Tidak ada payment yang bisa di-match.</b> Lihat kolom <b>Status</b> di tabel di atas:
                  <ul className="list-disc ml-4 mt-1 space-y-0.5">
                    <li><b>TAGIHAN TIDAK ADA</b> → buka menu <b>Tagihan</b>, buat tagihan untuk siswa+nomenklatur tersebut, lalu jalankan Rematch lagi.</li>
                    <li><b>SEMUA PAID</b> → tagihan sudah lunas; kemungkinan payment ini duplikat. Cek atau hapus payment via aksi 🗑 di tabel Pembayaran.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setRematchOpen(false)} disabled={rematchBusy}>Tutup</Button>
            <Button onClick={runRematch} disabled={rematchBusy || !rematchPreview || rematchPreview.matched_count === 0} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="rematch-confirm-button">
              {rematchBusy ? "Memproses..." : `Jalankan Rematch (${rematchPreview?.matched_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
